/* eslint-disable max-lines */
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Hidden from '@material-ui/core/Hidden';
import {
  IStateList,
  ListComponent,
  TableCellSortable,
} from 'components/Abstract/List';
import Toolbar from 'components/Layout/Toolbar';
import TableWrapper from 'components/Shared/TableWrapper';
import { WithStyles } from 'decorators/withStyles';
import { IDocument } from 'interfaces/document';
import { IEntity } from 'interfaces/entity';
import { IPaginationParams } from 'interfaces/pagination';
import React, { Fragment } from 'react';
import { of } from 'rxjs';
import RxOp from 'rxjs-operators';
import entityService from 'services/entity';
import selectedVentureService from 'services/profile';
import reportService from 'services/report';

import ListItem from './ListItem';
import Box from '@material-ui/core/Box';

interface IState extends IStateList<any> {
  current?: IDocument;
  formOpened?: boolean;
  entities?: IEntity[];
  ventureId?: number;
  reportURL?: string;
}
interface IProps {
  classes?: any;
}

@WithStyles((theme) => ({
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))
export default class AnalyticalReport extends ListComponent<IProps, IState> {

  timer: NodeJS.Timeout = null;

  constructor(props: {}) {
    super(props, 'order');
    this.state = {
      ...this.state,
      entities: [] as IEntity[],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    this && this.timer && clearTimeout(this.timer);
  }

  loadData = (params?: Partial<IPaginationParams>) => {

    this.setState({ loading: true, error: null });

    selectedVentureService
      .getSelectedVenture()
      .pipe(RxOp.logError(), RxOp.bindComponent(this))
      .subscribe(
        (selection) => {
          if (selection) {
            entityService
              .fetchEntitiesGraph(selection.id)
              .pipe(RxOp.logError(), RxOp.bindComponent(this))
              .subscribe(
                (entities) => {
                  this.setState({ entities: entities.data });
                },
                (err) => this.setError(err)
              );
            reportService
              .report(this.mergeParams(params), selection.id, 'analytical')
              .pipe(
                RxOp.logError(),
                RxOp.bindComponent(this),
                RxOp.tap(() => this.setState({ ventureId: selection.id })),
                RxOp.map((items) => this.setPaginatedData(items)),
                RxOp.catchError((error) => of(this.setError(error)))
              )
              .subscribe();
          }
        },
        (err) => this.setError(err)
      );
  };

  includes = (item: string, term: string) => {
    const regex = new RegExp(term, 'i');
    return regex.test(item);
  }

  isIncluded = (items: string[], term: string) => {
    return items.some((item) => this.includes(item, term));
  }

  filter = () => {
    const { term } = this.state;

    if (this.state.page === 0) {
      this.loadData({
        searchBy: term,
        page: this.state.page,
      });
      return;
    }

    const metrics = this.state.allItems.map((item) => ({
      ...item,
      filtered: !this.isIncluded([
        item.entity,
        item.name,
        item.filename,
      ], term),
    }));

    this.setState({
      allItems: metrics,
      items: metrics.filter((item) => !item.filtered),
      page: 0,
    });
  };

  handleChangeTerm = (term: string) => {
    if (this.state.loading) return;

    this.setState({ term });
    clearTimeout(this.timeoutTerm);

    if (term && term.length < 3) return;

    this.timeoutTerm = setTimeout(() => this.filter(), 600);
  };

  exportReport =
    (reportKind = 'analytical') =>
      () => {
        const { ventureId } = this.state;
        if (!ventureId) return;

        this.setState({ loading: true });
        reportService
          .exportXLS(ventureId, reportKind)
          .pipe(
            RxOp.logError(),
            RxOp.bindComponent(this),
            RxOp.catchError((err) => of({}))
          )
          .subscribe(
            (data) => {
              this.setState({
                reportURL: data.reportURL ? data.reportURL : undefined,
                loading: false,
              });
              this.timer = setTimeout(
                () =>
                  this.setState({
                    reportURL: undefined,
                    loading: false,
                  }),
                2500
              );
            },
            (err) => this.setError(err)
          );
      };

  render() {

    const { items, entities, reportURL, loading, ventureId } = this.state;

    return (
      <Fragment>
        <Toolbar
          title="Relatório Analítico"
          breadcrumbs={[
            { title: 'Documentação' },
            { title: 'Relatório Analítico' },
          ]}
        />
        {reportURL && <iframe hidden title={reportURL} src={reportURL} />}
        <Container>
          <Card>
            {this.renderLoader()}
            <CardContent>
              <Grid container alignItems="center" justify="space-between">
                <Grid item xs sm={8} lg={4}>
                  {this.renderSearch()}
                </Grid>
                <Grid item xs="auto">
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <Button
                      disabled={loading || !ventureId}
                      aria-controls="exportar-relatorio"
                      aria-haspopup="false"
                      variant="contained"
                      color="primary"
                      onClick={this.exportReport()}
                    >
                      <Hidden xsDown>Exportar</Hidden> Relatório completo
                    </Button>
                    <Button
                      disabled={loading || !ventureId}
                      aria-controls="exportar-relatorio"
                      aria-haspopup="false"
                      variant="contained"
                      color="primary"
                      style={{ marginLeft: 10 }}
                      onClick={this.exportReport('analytical-current')}
                    >
                      <Hidden xsDown>Exportar</Hidden> Relatório ativos
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TableWrapper minWidth={900}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="entityId"
                          >
                            Módulo
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="order"
                            align="right"
                          >
                            Ordem
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="tabId"
                          >
                            Aba
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="subTabId"
                          >
                            Subaba
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="name"
                          >
                            Documento
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="filename"
                          >
                            Arquivo
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="emissionDate"
                          >
                            Emissão
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="expirationDate"
                          >
                            Vencimento
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="expirationDate"
                          >
                            Tempo de Vencimento
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="frequency"
                          >
                            Periodicidade
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="status"
                          >
                            Status
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="status"
                            align="center"
                          >
                            Situação
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="uploadDate"
                          >
                            Comentários
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="uploadDate"
                          >
                            Upload
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="validationDate"
                            align="right"
                          >
                            Validação
                          </TableCellSortable>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {this.renderEmptyAndErrorMessages(14)}
                        {entities &&
                          entities.length > 0 &&
                          items
                            .filter((file) => !file.filtered)
                            .map((file) => (
                              <ListItem
                                key={file.id || file.documentId}
                                entities={entities}
                                file={file}
                              />
                            ))}
                      </TableBody>
                    </Table>
                  </TableWrapper>
                  {this.renderTablePagination()}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }
}
